// Single source of truth for the section-type -> JTBD role mapping (ADR-0119).
//
// ⚠️ THIS MODULE MUST STAY ZOD-FREE — and dependency-free generally.
// `section-registry.tsx` imports it, and that file is CLIENT-BUNDLED
// (`registry/index.ts` re-exports `renderSection`; `admin/src/client/
// preview-main.tsx` imports it directly). Pulling zod in here would ship the
// validation graph to every visitor. `section-components.ts` records the same
// rule for the component map.
//
// The role is load-bearing at RUNTIME, not documentation:
//   1. `section-registry.tsx` gates the ADR-0069 headline bandit on
//      `role === "hero"`, deliberately so any hero works regardless of `type`.
//   2. It is emitted as `data-section-role` and read by `utils/tracking.ts`
//      into the `section_role` telemetry field.
// So a MISSING role is a production defect (degraded analytics, a hero the
// bandit can't see), not a docs gap. Before ADR-0119 this map lived inline in
// `section-registry.tsx` and `kit-error-teaser` was absent — it reported
// `section_role: "unknown"` in production.
//
// `admin/scripts/build-section-catalog.mjs` imports this module for the MCP
// catalog's `jtbd_role`. It previously read a hand-maintained mirror at
// `admin/src/server/catalog/section-roles.ts` that had drifted to 1-of-54
// still-registered entries; that file is deleted.
//
// Coverage is asserted by `tests/unit/section-catalog-parity.test.ts`: every
// registered section type must appear here.

/**
 * The closed JTBD role vocabulary. Deliberately a fixed tuple: widening it is a
 * taxonomy decision (it changes how every analytics query buckets sections and
 * how a future palette groups them), not a mechanical step when adding a
 * section. Reuse an existing role unless the new section genuinely does a job
 * none of these describe.
 */
export const SECTION_ROLES = [
  "nav",
  "hero",
  "pain-diagnosis",
  "authority",
  "value-prop",
  "qualifier",
  "process",
  "social-proof",
  "pricing",
  "objection-handling",
  "cross-sell",
  "lead-capture",
  "closer",
  "content",
  "footer",
  "utility",
] as const;

export type SectionRole = (typeof SECTION_ROLES)[number];

/**
 * Fallback for a section whose type carries no role. Kept as a named export so
 * the parity test and the catalog generator assert against the same literal
 * rather than two copies of the string.
 */
export const UNKNOWN_SECTION_ROLE = "unknown";

// Declared with `satisfies` so every value is checked against SectionRole at
// THIS declaration (a typo'd role fails `tsc:check`), then re-typed on export
// so callers can index it with an arbitrary string.
const roles = {
  // nav
  "kit-navbar": "nav",

  // hero
  "kit-guide-hero": "hero",
  "kit-hero-portrait": "hero",
  "kit-hero-simple": "hero",
  "kit-home-hero": "hero",
  "kit-sale-hero": "hero",

  // pain-diagnosis
  "kit-home-pain": "pain-diagnosis",
  "kit-market-stat": "pain-diagnosis",
  // Names one concrete mistake ("Ошибка № 2 из 8") and shows до→после. Grouped
  // with the other "here is your problem" sections rather than with
  // `kit-before-after` (process), because on both live pages it diagnoses a
  // pain the reader recognizes. Judgment call, ADR-0119.
  "kit-error-teaser": "pain-diagnosis",

  // authority
  "kit-about-tags": "authority",

  // value-prop
  "kit-benefits": "value-prop",
  "kit-home-services": "value-prop",
  "kit-inside-showcase": "value-prop",
  "kit-links": "value-prop",
  "kit-program": "value-prop",
  "kit-takeaways": "value-prop",
  "kit-sale-bonus": "value-prop",

  // qualifier
  "kit-quiz": "qualifier",
  "kit-trainer": "qualifier",

  // process
  "kit-before-after": "process",
  "kit-process": "process",

  // social-proof
  "kit-case": "social-proof",
  "kit-reviews": "social-proof",
  "kit-stats": "social-proof",

  // pricing
  "kit-offer-formats": "pricing",
  "kit-service-tiers": "pricing",
  "kit-sale-tiers": "pricing",

  // objection-handling
  "kit-faq": "objection-handling",

  // cross-sell
  "kit-cross-sell": "cross-sell",
  "kit-guide-services": "cross-sell",

  // lead-capture
  "kit-checklist-island": "lead-capture",
  "kit-gift-island": "lead-capture",
  "kit-sale-leadmagnet": "lead-capture",

  // closer
  "kit-countdown": "closer",
  "kit-final-panel": "closer",
  "kit-guide-final-cta": "closer",
  "lead-magnet-next-steps": "closer",

  // content
  "kit-markdown": "content",

  // footer
  footer: "footer",
  "kit-footer": "footer",

  // utility
  "thank-you-redirect": "utility",
} satisfies Record<string, SectionRole>;

export const SECTION_TYPE_ROLES: Record<string, SectionRole> = roles;

/** Role for a section type, or `"unknown"` when the type carries none. */
export function roleForSectionType(type: string): SectionRole | typeof UNKNOWN_SECTION_ROLE {
  return SECTION_TYPE_ROLES[type] ?? UNKNOWN_SECTION_ROLE;
}
